import { useEffect } from "react";
import { TextField, Button, Grid, Typography } from "@mui/material";
import { ErrorMessage, Field, FormikProps, FormikProvider, useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Alert } from "@mui/material";
import { Map as LeafletMap } from "leaflet";

import { APIError } from "src/types";
import { ButtonGrid } from "src/components/form-components/ButtonGrid";
import { ButtonLoading } from "src/components/ButtonLoading";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { useResidentialZoneSchema } from "src/schemas/residential-zone.schema";
import { ResidentialZoneMutationData } from "src/hooks/residential-zones";
import { useDrawPolygon } from "src/hooks/useDrawPolygon";
import { featureToWKT, wktPolygonToFeature } from "src/utils/geometry.utils";

export type ResidentialZoneFormData = ResidentialZoneMutationData | ResidentialZoneMutationData;

interface Props {
  map: LeafletMap;
  title?: string;
  data: ResidentialZoneFormData;
  readOnly?: boolean;
  isLoading: boolean;
  errors?: APIError[] | null;
  formRef?: React.MutableRefObject<FormikProps<ResidentialZoneFormData> | null>;
  onClose: () => void;
  onSubmit: (values: ResidentialZoneFormData) => any;
  onManageVehicleRestrictions?: () => void;
}

export function ResidentialZoneForm(props: Props) {
  const { t } = useTranslation();
  const validationSchema = useResidentialZoneSchema();

  const {
    polygon,
    renderInitialPolygon,
    startDrawing,
    stopDrawing,
    stopEditDrawing,
    redrawDrawing,
    editDrawing,
    removeAllDrawings,
  } = useDrawPolygon(props.map);

  const initialValues: ResidentialZoneFormData = {
    name: props.data.name,
    polygon_frontendSrid: props.data.polygon_frontendSrid,
  };

  const formik = useFormik({
    innerRef: props.formRef,
    initialValues,
    validationSchema,
    onSubmit: async (values: ResidentialZoneFormData) => {
      stopEditDrawing();
      return props.onSubmit(values);
    },
  });

  /**
   * Effects
   */
  useEffect(persistDrawingInForm, [polygon]);
  useEffect(() => {
    if (props.data.polygon_frontendSrid) {
      const geometry = wktPolygonToFeature(props.data.polygon_frontendSrid);
      renderInitialPolygon(geometry);
    } else {
      startDrawing();
    }
  }, []);

  /**
   * Functions
   */
  function persistDrawingInForm() {
    if (!polygon) {
      formik.setFieldValue("polygon_frontendSrid", "");
    } else {
      const polygon_frontendSrid = featureToWKT(polygon);
      formik.setFieldValue("polygon_frontendSrid", polygon_frontendSrid);
    }
  }

  function handleRedraw() {
    redrawDrawing();
    formik.setTouched({ polygon_frontendSrid: false }, false);
  }

  function handleClose() {
    stopDrawing();
    stopEditDrawing();
    removeAllDrawings();
    props.onClose();
  }

  return render();

  function render() {
    return (
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {props.title && (
              <Grid item xs={12}>
                <Typography variant="h2">{props.title}</Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Field
                component={TextField}
                id="name"
                name="name"
                label={t("label.name")}
                type="text"
                variant="outlined"
                fullWidth
                disabled={props.readOnly}
                onChange={formik.handleChange}
                value={formik.values.name}
              />
            </Grid>

            {/* Location */}
            <Grid item xs={12}>
              <Typography variant="h3" gutterBottom>
                {t("label.location")}
              </Typography>
              {!polygon && <Typography>{t("label.drawTheResidentialZoneOnTheMap")}</Typography>}

              {polygon && (
                <>
                  <Typography gutterBottom>{t("label.residentialZoneIsVisualizedOnTheMap")}</Typography>

                  {!props.readOnly && (
                    <>
                      <Button color="primary" onClick={handleRedraw}>
                        {t("label.redraw")}
                      </Button>
                      <Button color="primary" onClick={editDrawing}>
                        {t("label.edit")}
                      </Button>
                    </>
                  )}
                </>
              )}
            </Grid>
            <Grid item xs={12}>
              <ErrorMessage name="polygon_frontendSrid">
                {(message) => (
                  <Alert icon={false} severity="error">
                    {message}
                  </Alert>
                )}
              </ErrorMessage>
            </Grid>

            {props.errors && (
              <Grid item xs={12}>
                <ServerErrors data={props.errors} />
              </Grid>
            )}

            {!props.readOnly && (
              <>
                <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
                  <Button variant="text" onClick={handleClose}>
                    {t("label.cancel")}
                  </Button>
                  <ButtonLoading
                    disabled={!formik.isValid}
                    isLoading={formik.isSubmitting || props.isLoading}
                    type="submit"
                    color="primary"
                    variant="contained"
                  >
                    {t("label.save")}
                  </ButtonLoading>
                </ButtonGrid>
              </>
            )}
          </Grid>
        </form>
      </FormikProvider>
    );
  }
}
